import { doc, updateDoc, where } from 'firebase/firestore';
import {
    Contract,
    Section,
    Subsection,
    contractConverter,
    CONTRACT_COLLECTION_NAME,
    SECTION_COLLECTION_NAME,
    SUBSECTION_COLLECTION_NAME,
    COVERAGE_COLLECTION_NAME,
    coverageConverter,
    subsectionConverter,
    sectionConverter,
    CLAUSE_COLLECTION_NAME,
    clauseConverter,
    Clause,
    Coverage,
    PRODUCT_TYPE_COLLECTION_NAME,
    productTypeConverter,
    ProductType,
} from 'models';
import { Button } from 'components/button';
import { db } from 'firebaseStore';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { SectionsSidePanel } from './sectionsSidePanel';
import { ContractSidePanel } from './contractSidePanel';
import { CoveragesSidePanel } from './coveragesSidePanel';
import { usePrompt, wordContract } from 'utils';
import './contract.css';
import {
    CollectionProvider,
    CollectionState,
    useCollectionContext,
} from 'components/settings/collectionContext';
import { DocumentProvider } from 'components/settings/documentContext';
import { ContractContextProvider, useContract, useContractContext } from './contractContext';
import { useCommit } from 'commits';

export const ContractClausesPage = () => {
    const { contractId } = useParams();
    if (!contractId) {
        return <div>`Cannot load contract with id '${contractId}'`</div>;
    }
    return (
        <DocumentProvider
            collectionName={CONTRACT_COLLECTION_NAME}
            converter={contractConverter}
            id={contractId}
        >
            <ContractClausesPageContentWrapper />
        </DocumentProvider>
    );
};

export const useSection = (indexId: string) => {
    const [, , , collectionMap] = useCollectionContext(SECTION_COLLECTION_NAME);
    return collectionMap.get(indexId) as Section | undefined;
};
export const useSubsection = (indexId: string) => {
    const [, , , collectionMap] = useCollectionContext(SUBSECTION_COLLECTION_NAME);
    return collectionMap.get(indexId) as Subsection | undefined;
};
export const useCoverage = (indexId: string) => {
    const [contract] = useContract();
    const [, , , collectionMap] = useCollectionContext(COVERAGE_COLLECTION_NAME, [
        where('productTypeIndexIds', 'array-contains', contract?.productTypeIndexId),
    ]);
    return collectionMap.get(indexId) as Subsection | undefined;
};
export const useClause = (indexId: string) => {
    const [contract] = useContract();
    const [, , , collectionMap] = useCollectionContext(CLAUSE_COLLECTION_NAME, [
        where('productTypeIndexIds', 'array-contains', contract?.productTypeIndexId),
    ]);
    return collectionMap.get(indexId) as Subsection | undefined;
};

const ContractClausesPageContentWrapper = () => {
    const [contract, pending] = useContract();
    if (pending) {
        return <div>Loading contract</div>;
    }
    if (!contract) {
        return <div>Could not load contract</div>;
    }
    return (
        <CollectionProvider collectionName={SECTION_COLLECTION_NAME} converter={sectionConverter}>
            <CollectionProvider
                collectionName={PRODUCT_TYPE_COLLECTION_NAME}
                converter={productTypeConverter}
            >
                <CollectionProvider
                    collectionName={SUBSECTION_COLLECTION_NAME}
                    converter={subsectionConverter}
                >
                    <CollectionProvider
                        collectionName={COVERAGE_COLLECTION_NAME}
                        converter={coverageConverter}
                        constraints={[
                            where(
                                'productTypeIndexIds',
                                'array-contains',
                                contract.productTypeIndexId
                            ),
                        ]}
                    >
                        <CollectionProvider
                            collectionName={CLAUSE_COLLECTION_NAME}
                            converter={clauseConverter}
                            constraints={[
                                where(
                                    'productTypeIndexIds',
                                    'array-contains',
                                    contract.productTypeIndexId
                                ),
                            ]}
                        >
                            <ContractContextProvider>
                                <ContractClausesPageContent />
                            </ContractContextProvider>
                        </CollectionProvider>
                    </CollectionProvider>
                </CollectionProvider>
            </CollectionProvider>
        </CollectionProvider>
    );
};

const ExportComponent = () => {
    const [contract] = useContract();
    const [, , , sectionMap] = useCollectionContext(
        SECTION_COLLECTION_NAME
    ) as CollectionState<Section>;
    const [, , , subsectionMap] = useCollectionContext(
        SUBSECTION_COLLECTION_NAME
    ) as CollectionState<Subsection>;
    const [, , , coverageMap] = useCollectionContext(COVERAGE_COLLECTION_NAME, [
        where('productTypeIndexIds', 'array-contains', contract?.productTypeIndexId),
    ]) as CollectionState<Coverage>;
    const [, , , clauseMap] = useCollectionContext(CLAUSE_COLLECTION_NAME, [
        where('productTypeIndexIds', 'array-contains', contract?.productTypeIndexId),
    ]) as CollectionState<Clause>;
    return (
        <Button
            style={{ width: '97px' }}
            onClick={() =>
                wordContract(contract, sectionMap, subsectionMap, coverageMap, clauseMap)
            }
        >
            Export
        </Button>
    );
};

const ContractClausesPageContent = () => {
    const { contract, originalContract, setExpCol, expCol } = useContractContext();
    const [, , , productTypesMap] = useCollectionContext(
        PRODUCT_TYPE_COLLECTION_NAME
    ) as CollectionState<ProductType>;
    const hasChanged = !contract.equals(originalContract);
    usePrompt('Unsaved changes - are you sure?', hasChanged);
    const navigate = useNavigate();
    const commit = useCommit<Contract>();
    const onSave = async (newerContract: Contract, next?: boolean) => {
        if (!hasChanged) {
            console.warn('not saving - has not changed');
            return next && navigate(`/contract-languages/${contract.id}`);
        }
        const docRef = doc(db, CONTRACT_COLLECTION_NAME, contract.id);
        await commit({
            previous: originalContract,
            current: newerContract,
            action: 'update',
            message: '',
        });
        return updateDoc(docRef, newerContract.toFirestore()).then(
            () => next && navigate(`/contract-languages/${contract.id}`),
            (error) => {
                console.error('ContractClausesPageContent updateDoc error', error);
            }
        );
    };
    return (
        <div>
            <div style={{ display: 'flex', marginBottom: '15px' }}>
                <div
                    style={{
                        flexBasis: '373px',
                        flexGrow: '1',
                        marginLeft: '100px',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <div style={{ fontSize: '22px' }}>
                        {contract.name} - {productTypesMap.get(contract.productTypeIndexId)?.name}
                    </div>
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        fontSize: '34px',
                        flexBasis: '373px',
                        flexGrow: '1',
                    }}
                >
                    Create new document
                </div>
                <div
                    style={{
                        marginRight: '100px',
                        marginLeft: '40px',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-end',
                        flexBasis: '373px',
                        flexGrow: '1',
                    }}
                >
                    <div
                        style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            marginRight: '16px',
                            minWidth: '65px',
                        }}
                        onClick={() => {
                            const nextAllExpCol =
                                expCol.getValue('all') !== 'expanded' ? 'expanded' : 'collapsed';
                            setExpCol('all', nextAllExpCol);
                        }}
                    >
                        {expCol.getValue('all') !== 'expanded' ? 'Expand all' : 'Collapse All'}
                    </div>

                    <Button
                        style={{ marginRight: '16px', width: '97px' }}
                        onClick={() => onSave(contract)}
                    >
                        Save
                    </Button>
                    <ExportComponent />
                    <NavLink
                        to={`/contract/${encodeURIComponent(contract.id)}`}
                        style={{ color: 'inherit', textDecoration: 'inherit' }}
                    >
                        <Button style={{ marginLeft: '16px', width: '97px' }}>Back</Button>
                    </NavLink>
                    <Button
                        style={{ marginLeft: '16px', width: '97px' }}
                        onClick={() => onSave(contract, true)}
                    >
                        Next
                    </Button>
                </div>
            </div>
            <div style={{ display: 'flex', height: 'calc(100vh - 197px)' }}>
                <div
                    style={{
                        flexBasis: '373px',
                        flexGrow: '1',
                        marginLeft: '100px',
                        height: '100%',
                        overflow: 'scroll',
                    }}
                >
                    <SectionsSidePanel />
                    <CoveragesSidePanel />
                </div>
                <div
                    style={{
                        flexBasis: '846px',
                        marginRight: '100px',
                        marginLeft: '40px',
                        flexGrow: '1',
                        height: '100%',
                        overflow: 'scroll',
                    }}
                >
                    <ContractSidePanel showLabel={true} />
                </div>
            </div>
        </div>
    );
};
